/* eslint-disable import/no-cycle */
import React, { useMemo, useState } from 'react'

import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { v4 as uuid } from 'uuid'

import { changeModalState } from 'actions/modalActions'
import aerolineasLogo from 'assets/AerolineasLogo.svg'
import DatePicker from 'components/datepicker/date-picker'
import DatePickerResponsive from 'components/datepicker/date-picker-responsive'
import DateRangePicker from 'components/datepicker/date-range-picker'
import DateRangePickerResponsive from 'components/datepicker/date-range-picker-responsive'
import Icon, { IconNames } from 'components/icons'
import Modal from 'components/modal'
import theme from 'components/theme'
import FORMATS from 'constants/formats'
import mediaTypes from 'constants/mediaTypes'
import MODAL_NAMES from 'constants/modalNames'
import { addMonthsFromToday, getDayJsObject } from 'helpers/dates'
import { isMobileOrTablet, isMobile } from 'helpers/utils'
import { parseToGeneralLanguage } from 'localization/helpers'
import { mapMediaType } from 'selectors/mediaType'

import {
  DatePickersContainer,
  OpenDateResponsivePickerOnly,
  ButtonCloseContainer,
  ImageLogo,
  ImageLogoContainer,
  ModalContent,
  ModalDatePickerContainer,
  ModalHeader,
  ModalTitle,
  TitleIconContainer,
  TitleText
} from './styled'
import { getTranslatedDay, getTranslatedMonth, getTranslatedWeekDay } from '../helpers'
import YearMonthForm from '../year-month-form'

const DatePickerFullResponsive = props => {
  const {
    t,
    i18n: { language }
  } = useTranslation()

  const [today] = useState(getDayJsObject().toDate())
  const [selectedDate, setSelectedDate] = useState(undefined)

  const {
    showDatePickerAbove,
    error,
    label,
    showRange,
    dateToError,
    showTitleMobile,
    value,
    modalName: parseModalName,
    onChange,
    mediaType,
    format,
    placeholder,
    placeholderMobile,
    disabledDays,
    changeModalState,
    formatMobile,
    minDate,
    maxDate,
    month,
    width,
    useMonthOverMinDate,
    langcode,
    initialMonth,
    disabled,
    hasYearMonthForm,
    onBlur,
    id,
    ...rest
  } = props

  const modalName = useMemo(() => parseModalName + uuid(), [parseModalName])

  const handleOnChange = value => {
    onChange && onChange(value)

    setSelectedDate(value)

    if (isMobile(mediaType)) {
      hasYearMonthForm
        ? selectedDate &&
          value.getMonth() == selectedDate.getMonth() &&
          value.getFullYear() == selectedDate.getFullYear() &&
          onModalDatePickerClose()
        : onModalDatePickerClose()
    }
  }

  const getDatePlaceholder = () => {
    return isMobileOrTablet(mediaType) && placeholderMobile
      ? placeholderMobile
      : placeholder || FORMATS.DAY_MONTH_YEAR
  }

  const getDateFormat = () => {
    return isMobileOrTablet(mediaType) && formatMobile
      ? FORMATS.DAY_MONTH_YEAR_SHORT
      : format || FORMATS.DAY_MONTH_YEAR
  }

  const getMonth = () => month || selectedDate || initialMonth || minDate || today

  const getMinDate = () => {
    if (useMonthOverMinDate && month) {
      return undefined
    }

    return minDate || today
  }

  const getInitialMonth = () => {
    return initialMonth || minDate || today
  }
  const getMaxDate = () => maxDate || addMonthsFromToday(11).toDate()

  const getDisabledDays = () => {
    return disabledDays || [{ before: getMinDate(), after: getMaxDate() }]
  }

  const isMobileOpenModalDatePicker = () => {
    if (!disabled && isMobile(mediaType)) {
      changeModalState({
        isVisible: true,
        modalName
      })
    }
  }

  const onModalDatePickerClose = () =>
    changeModalState({
      isVisible: false,
      modalName
    })

  const locale = langcode || parseToGeneralLanguage(language)

  const dayPickerProps = {
    months: getTranslatedMonth(t),
    weekdaysLong: getTranslatedWeekDay(t),
    weekdaysShort: getTranslatedDay(t)
  }

  /*
  Use captionElement in DatePickers to allow selecting month and year
  with an internal calendar form
  */
  const captionElement = ({ date }) => (
    <YearMonthForm
      date={date}
      months={dayPickerProps.months}
      onChange={value => handleOnChange(value)}
      toYear={props.toYear}
      minDate={minDate}
    />
  )

  const getModalName = modalName =>
    modalName.includes(MODAL_NAMES.BIRTHDAY)
      ? t('general.date-picker.birthday')
      : modalName.includes(MODAL_NAMES.SUBLOS_EXPIRATION_DATE)
      ? t('general.date-picker.expiration-date')
      : t('general.date-picker.choose-travel-dates')

  return (
    <DatePickersContainer width={width}>
      <OpenDateResponsivePickerOnly onClick={() => isMobileOpenModalDatePicker()} />
      {!showRange && (
        <DatePicker
          showDatePickerAbove={showDatePickerAbove}
          label={label || t('general.date-picker.date')}
          showTitle={showTitleMobile}
          onChange={value => handleOnChange(value)}
          onBlur={onBlur}
          disabledDays={getDisabledDays()}
          fromError={error}
          locale={locale}
          format={getDateFormat()}
          placeholder={getDatePlaceholder()}
          name="from-date"
          id={id}
          value={value || selectedDate}
          fromMonth={getMinDate()}
          toMonth={getMaxDate()}
          month={getMonth()}
          initialMonth={getInitialMonth()}
          dayPickerProps={dayPickerProps}
          disabled={disabled}
          captionElement={hasYearMonthForm && captionElement}
          {...rest}
        />
      )}

      {showRange && (
        <DateRangePicker
          fromLabel={t('general.date-picker.date-from')}
          toLabel={t('general.date-picker.date-to')}
          value={value || selectedDate}
          onChange={value => handleOnChange(value)}
          disabledDays={getDisabledDays()}
          fromError={error}
          toError={dateToError}
          locale={locale}
          format={FORMATS.DAY_PICKER_FORMAT}
          placeholder={t('general.date-picker.date-format')}
          fromMonth={getMinDate()}
          toMonth={getMaxDate()}
          month={getMonth()}
          initialMonth={getInitialMonth()}
          width={width}
          dayPickerProps={dayPickerProps}
          {...rest}
        />
      )}
      <Modal name={modalName} onClose={() => onModalDatePickerClose()} autoWidth>
        <ModalContent>
          <ModalHeader>
            <ImageLogoContainer>
              <ImageLogo>
                <img src={aerolineasLogo} alt="aerolineas.com" />
              </ImageLogo>
            </ImageLogoContainer>
            <ButtonCloseContainer onClick={() => onModalDatePickerClose()}>
              <Icon name={IconNames.Close} color={theme.colors.blue50} size={30} />
            </ButtonCloseContainer>
          </ModalHeader>
          <ModalTitle>
            <TitleIconContainer>
              <Icon name={IconNames.Calendar} color={theme.colors.blue50} size={30} />
            </TitleIconContainer>
            <TitleText>{getModalName(modalName)}</TitleText>
          </ModalTitle>
          <ModalDatePickerContainer>
            {!showRange && (
              <DatePickerResponsive
                label={t('general.date-picker.date')}
                onChange={value => handleOnChange(value)}
                disabledDays={getDisabledDays()}
                fromError={error}
                locale={locale}
                format={getDateFormat()}
                placeholder={getDatePlaceholder()}
                value={value || selectedDate}
                fromMonth={getMinDate()}
                toMonth={getMaxDate()}
                initialMonth={getInitialMonth()}
                dayPickerProps={dayPickerProps}
                disabled={disabled}
                numberOfMonths={hasYearMonthForm && 1}
                captionElement={hasYearMonthForm && captionElement}
                month={hasYearMonthForm && getMonth()}
              />
            )}
            {showRange && (
              <DateRangePickerResponsive
                value={value || selectedDate}
                onChange={value => handleOnChange(value)}
                disabledDays={getDisabledDays()}
                fromError={error}
                locale={locale}
                format={FORMATS.DAY_PICKER_FORMAT}
                fromMonth={getMinDate()}
                toMonth={getMaxDate()}
                initialMonth={getInitialMonth()}
                dayPickerProps={dayPickerProps}
              />
            )}
          </ModalDatePickerContainer>
        </ModalContent>
      </Modal>
    </DatePickersContainer>
  )
}

const mapStateToProps = state => ({
  mediaType: mapMediaType(state)
})

const mapDispatchToProps = {
  changeModalState
}

DatePickerFullResponsive.propTypes = {
  centeredTitle: PropTypes.bool,
  showTitleMobile: PropTypes.bool,
  hideTitleOnDesktop: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  placeholderMobile: PropTypes.string,
  format: PropTypes.oneOf(Object.values(FORMATS)),
  formatMobile: PropTypes.oneOf(Object.values(FORMATS)),
  light: PropTypes.bool,
  index: PropTypes.number,
  disableIndex: PropTypes.bool,
  showRange: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  dateToError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  flatStyle: PropTypes.bool,
  semiBold: PropTypes.bool,
  width: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.instanceOf(Date)]),
  month: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  disabledDays: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.array]),
  modalName: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  changeModalState: PropTypes.func,
  mediaType: PropTypes.oneOf(Object.values(mediaTypes)),
  useMonthOverMinDate: PropTypes.bool,
  langcode: PropTypes.string,
  disabled: PropTypes.bool
}

export default connect(mapStateToProps, mapDispatchToProps)(DatePickerFullResponsive)
